import { database, storage } from './core';
import { ensureDatabaseOnline, executeWithRetry } from './database-connection';
import { getCurrentUser } from './auth.service';
import logger from '../../utils/logger';
import { subscriptionManager } from './subscription-manager';
import { 
  ref, 
  get as firebaseGet, 
  set as firebaseSet, 
  update as firebaseUpdate, 
  remove as firebaseRemove, 
  push, 
  query, 
  orderByChild, 
  equalTo, 
  limitToFirst, 
  limitToLast,
  startAt,
  endAt,
  onValue,
  off,
  Query,
  DataSnapshot
} from 'firebase/database';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  UploadResult
} from 'firebase/storage';

// Export root reference to match original app
export const dbRoot = ref(database);
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _storageRoot = storageRef(storage);

/**
 * Fetches data from a specific path in the database
 * @param path - The path to the data in the database
 * @returns The data at the specified path or null if it doesn't exist
 */
export const getData = async (path: string): Promise<any> => {
  try {
    // Ensure user is authenticated before attempting database access
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    // For stats paths, ensure user can only access their own data
    if (path.startsWith('/stats/') || path.startsWith('stats/')) {
      const cleanPath = path.startsWith('/') ? path.substring(1) : path;
      const pathSegments = cleanPath.split('/');
      if (pathSegments.length >= 2) {
        const requestedUserId = pathSegments[1];
        if (requestedUserId !== currentUser.uid) {
          logger.warn(`User ${currentUser.uid} attempted to access stats for user ${requestedUserId}`);
          throw new Error('Permission denied: can only access own stats');
        }
      }
    }
    
    const dataRef = ref(database, path);
    const snapshot = await firebaseGet(dataRef);
    const result = snapshot.exists() ? snapshot.val() : null;
    return result;
  } catch (error: any) {
    // Enhanced error logging for permission issues
    if (error?.code === 'PERMISSION_DENIED' || error?.message?.includes('Permission denied')) {
      const currentUser = getCurrentUser();
      logger.error(`Permission denied accessing ${path}. Auth state: ${currentUser ? 'authenticated as ' + currentUser.uid : 'not authenticated'}`);
      logger.error('Full error:', error);
    } else {
      logger.error(`Error fetching data from ${path}:`, error);
    }
    throw error;
  }
};

/**
 * Updates data at a specific path in the database
 * Matches the original Marune app's update function - if path is null/empty, updates at root
 * @param path - The path to update (null for root updates)
 * @param data - The data to update with
 * @returns A promise that resolves when the update is complete
 */
export const updateData = async (path: string | null, data: any): Promise<void> => {
  // C6 Fix: Check auth BEFORE retry loop - no point retrying without auth
  requireAuth();

  return executeWithRetry(async () => {
    // Ensure database is online before write operation
    await ensureDatabaseOnline();
    
    // Match original behavior: if path is null, update at root
    const dataRef = path ? ref(database, path) : ref(database);
    
    // Sanitize data to ensure no undefined values which cause Firebase errors
    const sanitizedData = JSON.parse(JSON.stringify(data));
    
    // Special handling for author object displayName which causes validation errors
    if (sanitizedData && sanitizedData.author && sanitizedData.author.displayName === undefined) {
      delete sanitizedData.author.displayName;
    }
    
    return await firebaseUpdate(dataRef, sanitizedData);
  });
};

/**
 * Sets data at a specific path in the database (overwrites existing data)
 * @param path - The path to set data at
 * @param data - The data to set
 * @returns A promise that resolves when the set operation is complete
 */
export const setData = async (path: string, data: any): Promise<void> => {
  // C6 Fix: Check auth BEFORE retry loop - no point retrying without auth
  requireAuth();

  return executeWithRetry(async () => {
    // Ensure database is online before write operation
    await ensureDatabaseOnline();
    
    const dataRef = ref(database, path);
    return firebaseSet(dataRef, data);
  });
};

/**
 * Removes data at a specific path in the database
 * @param path - The path to remove data from
 * @returns A promise that resolves when the remove operation is complete
 */
export const removeData = async (path: string): Promise<void> => {
  // C6 Fix: Check auth BEFORE retry loop - no point retrying without auth
  requireAuth();

  return executeWithRetry(async () => {
    // Ensure database is online before write operation
    await ensureDatabaseOnline();
    
    const dataRef = ref(database, path);
    return firebaseRemove(dataRef);
  });
};

/**
 * Creates data with an auto-generated key
 * @param path - The path to create data at
 * @param data - The data to create
 * @returns The key of the newly created data
 */
export const createWithKey = async (path: string, data: any): Promise<string> => {
  // C6 Fix: Check auth BEFORE retry loop - no point retrying without auth
  requireAuth();

  // IDEMPOTENCY: Pre-generate the push key OUTSIDE the retry loop. push(ref)
  // with no value mints the key purely client-side (no network I/O). If the
  // key were minted inside executeWithRetry via push(ref, data), a write that
  // committed server-side but lost its ack would be retried with a NEW key,
  // creating duplicate records (comments, notifications, offline creates).
  // With a fixed ref, retries idempotently rewrite the same key.
  const newRef = push(ref(database, path));

  return executeWithRetry(async () => {
    // Ensure database is online before write operation
    await ensureDatabaseOnline();

    // Sanitize data to ensure no undefined values which cause Firebase errors
    const sanitizedData = JSON.parse(JSON.stringify(data));

    // Special handling for author object displayName which causes validation errors
    if (sanitizedData && sanitizedData.author && sanitizedData.author.displayName === undefined) {
      delete sanitizedData.author.displayName;
    }

    // set() on the pre-generated ref is equivalent to push(ref, data) with
    // the same key, but safe to retry
    await firebaseSet(newRef, sanitizedData);
    return newRef.key || '';
  });
};

/**
 * Creates data with auto-generated key and returns the Firebase reference
 * Matches original Marune app's create function
 * @param path - The path to create data at
 * @param data - The data to create
 * @returns Promise that resolves to the Firebase reference
 */
export const create = async (path: string, data: any) => {
  // C6 Fix: Check auth BEFORE retry loop - no point retrying without auth
  requireAuth();

  // IDEMPOTENCY: key pre-generated outside the retry loop so a retry after a
  // lost ack rewrites the SAME key instead of minting a duplicate
  // (see createWithKey above)
  const newRef = push(ref(database, path));

  return executeWithRetry(async () => {
    await ensureDatabaseOnline();
    await firebaseSet(newRef, data);
    return newRef;
  });
};

/**
 * Creates data with auto-generated key and returns the key
 * Matches original Marune app's createWithRef function
 * @param path - The path to create data at
 * @param data - The data to create
 * @returns Promise that resolves to the key
 */
export const createWithRef = async (path: string, data: any): Promise<string> => {
  // C6 Fix: Check auth BEFORE retry loop - no point retrying without auth
  requireAuth();

  // IDEMPOTENCY: key pre-generated outside the retry loop so a retry after a
  // lost ack rewrites the SAME key instead of minting a duplicate
  // (see createWithKey above)
  const newRef = push(ref(database, path));

  return executeWithRetry(async () => {
    await ensureDatabaseOnline();
    await firebaseSet(newRef, data);
    return newRef.key || '';
  });
};

/**
 * Generates a new key without writing data
 * Matches original Marune app's generateKey function
 * @param path - The path to generate a key for
 * @returns The new Firebase reference
 */
export const generateKey = (path: string) => {
  const dataRef = ref(database, path);
  return push(dataRef);
};

/**
 * Fetches data matching a specific query
 * @param path - The path to query
 * @param queryParams - The query parameters
 * @returns An array of matching data
 */
export const getDataByQuery = async (path: string, queryParams: {
  orderByChild?: string;
  equalTo?: any;
  startAt?: any;
  endAt?: any;
  limitToFirst?: number;
  limitToLast?: number;
}): Promise<any[]> => {
  try {
    const dataRef = ref(database, path);
    let dataQuery: Query = dataRef;
    
    if (queryParams.orderByChild) {
      dataQuery = query(dataQuery, orderByChild(queryParams.orderByChild));
    }
    
    if (queryParams.equalTo !== undefined) {
      dataQuery = query(dataQuery, equalTo(queryParams.equalTo));
    }
    
    if (queryParams.startAt !== undefined) {
      dataQuery = query(dataQuery, startAt(queryParams.startAt));
    }
    
    if (queryParams.endAt !== undefined) {
      dataQuery = query(dataQuery, endAt(queryParams.endAt));
    }
    
    if (queryParams.limitToFirst) {
      dataQuery = query(dataQuery, limitToFirst(queryParams.limitToFirst));
    }
    
    if (queryParams.limitToLast) {
      dataQuery = query(dataQuery, limitToLast(queryParams.limitToLast));
    }
    
    const snapshot = await firebaseGet(dataQuery);
    return snapshotToArray(snapshot);
  } catch (error) {
    logger.error(`Error querying data at ${path}:`, error);
    throw error;
  }
};

/**
 * Subscribes to data changes at a specific path
 * @param path - The path to subscribe to
 * @param callback - The callback to invoke when data changes
 * @returns A function to unsubscribe from the data changes
 */
let subscriptionSeq = 0;

export const subscribeToData = (path: string, callback: (data: any) => void): (() => void) => {
  try {
    const dataRef = ref(database, path);

    // onValue returns an unsubscribe scoped to this listener. A bare
    // off(dataRef) here used to detach EVERY listener at the path, so a
    // second subscriber to the same path killed the first — and the shared
    // manager key made adding the second subscription unsubscribe both.
    const unsubscribe = onValue(dataRef, (snapshot) => {
      const data = snapshot.exists() ? snapshot.val() : null;
      callback(data);
    }, (error) => {
      logger.error(`Error in subscription to ${path}:`, error);
      // Invoke callback with null to indicate error
      callback(null);
    });

    // Register with subscription manager for global cleanup, keyed uniquely
    // per subscription so same-path subscribers coexist
    const subscriptionKey = `api-wrapper:${path}:${++subscriptionSeq}`;
    subscriptionManager.add(subscriptionKey, unsubscribe);

    // Return unsubscribe function that also removes from manager
    return () => {
      subscriptionManager.remove(subscriptionKey);
    };
  } catch (error) {
    logger.error(`Error setting up subscription to ${path}:`, error);
    // Return a no-op unsubscribe function
    return () => {};
  }
};

/**
 * Uploads a file to Firebase Storage
 * On native platforms, uses native fetch to bypass CapacitorHttp
 * @param path - The path in storage to upload to
 * @param file - The file to upload
 * @returns The download URL of the uploaded file
 */
export const uploadFile = async (path: string, file: File): Promise<string> => {
  try {
    logger.info(`📤 [uploadFile] Starting upload to ${path}, file size: ${file.size} bytes`);

    // Check if we're on a native platform (iOS/Android)
    const isNative = typeof window !== 'undefined' && (window as any).Capacitor !== undefined;
    logger.info(`📤 [uploadFile] Platform: ${isNative ? 'NATIVE' : 'WEB'}`);

    if (isNative) {
      // On native, bypass CapacitorHttp by using native fetch with XMLHttpRequest polyfill disabled
      logger.info('📤 [uploadFile] Using native upload method to bypass CapacitorHttp...');

      // Use the native XMLHttpRequest instead of Capacitor's intercepted version
      const nativeFetch = (window as any).CapacitorWebFetch || window.fetch;

      // Get upload URL from Firebase
      const fileRef = storageRef(storage, path);
      const bucket = storage.app.options.storageBucket;
      const uploadUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o?name=${encodeURIComponent(path)}`;

      logger.info(`📤 [uploadFile] Upload URL: ${uploadUrl}`);

      // Get auth token
      const { auth } = await import('./core');
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      const token = await user.getIdToken();

      // Convert file to array buffer
      const arrayBuffer = await file.arrayBuffer();

      logger.info('📤 [uploadFile] Uploading via native fetch...');

      const response = await nativeFetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: arrayBuffer
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} ${errorText}`);
      }

      logger.info('📤 [uploadFile] Upload successful, getting download URL...');
      const url = await getDownloadURL(fileRef);
      logger.info(`📤 [uploadFile] Success! URL: ${url.substring(0, 50)}...`);
      return url;

    } else {
      // On web, use standard Firebase upload
      logger.info('📤 [uploadFile] Using standard Firebase upload...');
      const fileRef = storageRef(storage, path);
      const snapshot: UploadResult = await uploadBytes(fileRef, file);
      const url = await getDownloadURL(snapshot.ref);
      logger.info(`📤 [uploadFile] Success! URL: ${url.substring(0, 50)}...`);
      return url;
    }
  } catch (error) {
    logger.error(`❌ [uploadFile] Error uploading file to ${path}:`, error);
    throw error;
  }
};

/**
 * Deletes a file from Firebase Storage
 * @param path - The path to the file to delete
 * @returns A promise that resolves when the delete operation is complete
 */
export const deleteFile = async (path: string): Promise<void> => {
  try {
    const fileRef = storageRef(storage, path);
    return deleteObject(fileRef);
  } catch (error) {
    logger.error(`Error deleting file at ${path}:`, error);
    throw error;
  }
};

/**
 * Updates multiple paths in the database in a single atomic operation
 * Matches the original Marune app's updateMulti function exactly
 * @param updates - An object mapping paths to values
 * @returns A promise that resolves when the update operation is complete
 */
export const updateMultiple = async (updates: Record<string, any>): Promise<void> => {
  // C6 Fix: Check auth BEFORE write operation - no point attempting without auth
  requireAuth();

  try {
    // Ensure database is online before write operation
    await ensureDatabaseOnline();
    
    // Use the root reference and update, exactly like original app's dbRoot.update(updates)
    const dbRef = ref(database);
    return firebaseUpdate(dbRef, updates);
  } catch (error) {
    logger.error('Error performing multiple updates:', error);
    throw error;
  }
};

/**
 * Performs a multi-path update using array format
 * Matches original Marune app's updateMulti function signature
 * @param pathsAndData - Array of objects with path and data properties
 * @returns A promise that resolves when the update operation is complete
 */
export const updateMulti = async (pathsAndData: Array<{path: string, data: any}>): Promise<void> => {
  const updates = pathsAndData.reduce((acc, { path, data }) => ({
    ...acc,
    [path]: data,
  }), {});
  return updateMultipleSafe(updates);
};

/**
 * Safer version of updateMultiple that performs individual updates
 * This avoids root-level permission errors at the cost of atomicity
 * Use this when updateMultiple fails with permission errors
 * @param updates - An object containing the paths as keys and the data to update as values
 * @returns A promise that resolves when all update operations are complete
 */
export const updateMultipleSafe = async (updates: Record<string, any>): Promise<void> => {
  // C6 Fix: Check auth BEFORE write operations - no point attempting without auth
  requireAuth();

  try {
    // Ensure database is online before write operations
    await ensureDatabaseOnline();
    
    // Perform individual updates for each path
    const updatePromises = Object.entries(updates).map(([path, data]) => {
      if (data === null) {
        // Handle deletion
        return removeData(path);
      } else {
        // Handle update/set
        return setData(path, data);
      }
    });
    
    // Wait for all updates to complete
    await Promise.all(updatePromises);
  } catch (error) {
    logger.error('Error performing safe multiple updates:', error);
    throw error;
  }
};

// Compatibility aliases to match original app function names exactly
export const update = updateData;
export const set = setData;
export const remove = removeData;
export const get = getData;
export const getByChildValue = async (path: string, childPath: string, reference: any) => {
  return getDataByQuery(path, { orderByChild: childPath, equalTo: reference });
};
export const subscribe = subscribeToData;
export const unsubscribe = (path: string, callback: (data: any) => void) => {
  const dataRef = ref(database, path);
  return off(dataRef, 'value', callback);
};

/**
 * Helper to verify user is authenticated before write operations (C6 Fix)
 * Throws immediately if not authenticated - no point retrying without auth
 */
const requireAuth = (): void => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('User not authenticated. Please log in and try again.');
  }
};

// Storage compatibility aliases to match original app
export const addToStorage = async (path: string, data: File, metadata?: any) => {
  const fileRef = storageRef(storage, path);
  const snapshot = await uploadBytes(fileRef, data, metadata);
  return snapshot;
};

export const addToStorageFromDisk = async (storagePath: string, localFile: File, metadata?: any) => {
  // In web environment, both functions work the same way
  return addToStorage(storagePath, localFile, metadata);
};

export const removeFromStorage = deleteFile;

export const removeFromStorageByURL = async (url: string) => {
  // Extract the path from the URL
  const decodedUrl = decodeURIComponent(url);
  const pathMatch = decodedUrl.match(/o\/(.*?)\?/);
  if (pathMatch && pathMatch[1]) {
    const path = pathMatch[1].replace(/%2F/g, '/');
    return deleteFile(path);
  }
  throw new Error('Invalid storage URL');
};

export const getDownloadURLFromPath = async (path: string): Promise<string> => {
  const fileRef = storageRef(storage, path);
  return getDownloadURL(fileRef);
};

/**
 * Helper function to convert a Firebase DataSnapshot to an array
 * @param snapshot - The snapshot to convert
 * @returns An array of the data in the snapshot with keys included
 */
export const snapshotToArray = (snapshot: DataSnapshot): any[] => {
  const result: any[] = [];
  
  if (!snapshot.exists()) {
    return result;
  }
  
  snapshot.forEach((childSnapshot) => {
    const item = childSnapshot.val();
    item.key = childSnapshot.key;
    result.push(item);
  });
  
  return result;
};

/**
 * Helper function to convert a Firebase DataSnapshot to an object
 * @param snapshot - The snapshot to convert
 * @returns An object mapping keys to values
 */
export const snapshotToObject = (snapshot: DataSnapshot): Record<string, any> => {
  const result: Record<string, any> = {};
  
  if (!snapshot.exists()) {
    return result;
  }
  
  snapshot.forEach((childSnapshot) => {
    const key = childSnapshot.key;
    if (key) {
      result[key] = childSnapshot.val();
    }
  });
  
  return result;
};